
import { computed, defineComponent, nextTick, type PropType, ref, watch } from 'vue';
import AppError, { type VuelidateValidation } from '@/components/Error.vue';

export type InputValue = string | number | null;
export enum InputType {
    Text = 'text',
    Password = 'password',
    Email = 'email',
    Number = 'number',
    Textarea = 'textarea'
}

export default defineComponent({
    name: 'AppInput',
    components: { AppError },
    props: {
        modelValue: { type: [String, Number] as PropType<InputValue>, default: null },
        icon: { type: String, default: '' },
        type: { type: String as PropType<InputType | `${InputType}`>, default: InputType.Text },
        placeholder: { type: String, default: '' },
        autocomplete: { type: String, default: 'off' },
        disabled: { type: Boolean, default: false },
        readonly: { type: Boolean, default: false },
        rows: { type: Number, default: 2 },
        validations: { type: Object as PropType<VuelidateValidation>, default: undefined }
    },
    emits: ['update:modelValue'],

    setup(props, { emit }) {
        const textareaRef = ref<HTMLTextAreaElement | null>(null);
        const isTextarea = computed(() => props.type === InputType.Textarea);

        function onValueChange(event: Event): void {
            const target = event.target;
            if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
                return;
            }

            emit('update:modelValue', target.value);
            props.validations?.$touch();
        }

        const resizeTextarea = async (): Promise<void> => {
            const element = textareaRef.value;
            if (!element || !isTextarea.value) return;

            await nextTick();
            const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
            if (Number.isNaN(lineHeight)) return;

            const maxHeight = lineHeight * 10;
            element.style.height = Math.min(Math.max(element.scrollHeight, element.offsetHeight), maxHeight) + 'px';
            element.style.overflowY = element.scrollHeight > maxHeight ? 'auto' : 'hidden';
        };

        watch(() => props.modelValue, resizeTextarea, { immediate: true });

        return {
            textareaRef,
            isTextarea,
            onValueChange
        };
    }
});
